import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Save, Eye, Download, History, Settings,
    Bold, Italic, Underline, Link, Image, Video, Code, Quote,
    Heading1, Heading2, FileText,
    Loader, X, Plus, ChevronRight,
    Maximize2, Minimize2, Trash2, RotateCcw, FileX
} from 'lucide-react';
import useContentStore from '../../store/contentStore';

const ContentEditorPage: React.FC = () => {
    const {
        // State
        currentDocument,
        isLoading,
        isSaving,
        lastSaved,
        editorContent,
        wordCount,
        readingTime,
        hasUnsavedChanges,
        showPreview,
        showVersionHistory,
        showSEOPanel,
        showMediaLibrary,
        sidebarCollapsed,
        mediaLibrary,
        // collaborators,
        recentDocuments,
        currentVersion,


        // Actions
        createNewDocument,
        saveDocument,
        updateContent,
        updateTitle,
        updateSEOSettings,
        uploadMedia,
        insertMedia,
        deleteMedia,
        exportDocument,
        loadDocument,
        getAllDocuments,

        // Version control
        saveNewVersion,
        getVersions,
        restoreVersion,

        // Toggles
        togglePreview,
        toggleVersionHistory,
        toggleSEOPanel,
        toggleMediaLibrary,
        toggleSidebar,

        // Utility
        // generateSlug,
        resetEditor
    } = useContentStore();

    const [_selectedText, setSelectedText] = useState('');
    const [cursorPosition, setCursorPosition] = useState(0);
    const [showToolbar, setShowToolbar] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showNewDocumentModal, setShowNewDocumentModal] = useState(false);
    const [showSaveVersionModal, setShowSaveVersionModal] = useState(false);
    const [versionComment, setVersionComment] = useState('');
    const [newDocumentTitle, setNewDocumentTitle] = useState('');

    const editorRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Load a sample document on mount if none exists
        // if (!currentDocument) {
        //     createNewDocument({
        //         title: 'Untitled Document',
        //         content: '',
        //         tags: [],
        //         seoSettings: {
        //             title: '',
        //             metaDescription: '',
        //             keywords: [],
        //         },
        //     });
        // }
        getAllDocuments();
    }, [getAllDocuments]);

    // Load versions when version history is opened
    useEffect(() => {
        if (showVersionHistory && currentDocument) {
            getVersions();
        }
    }, [showVersionHistory, currentDocument?._id]);

    // Auto-save indication
    const formatLastSaved = () => {
        if (!lastSaved) return '';
        const now = new Date();
        const diff = Math.floor((now.getTime() - lastSaved.getTime()) / 1000);

        if (diff < 60) return `Auto-saved ${diff}s ago`;
        if (diff < 3600) return `Auto-saved ${Math.floor(diff / 60)}m ago`;
        return `Auto-saved ${Math.floor(diff / 3600)}h ago`;
    };

    // Text selection handling
    const handleTextSelection = useCallback(() => {
        if (editorRef.current) {
            const start = editorRef.current.selectionStart;
            const end = editorRef.current.selectionEnd;
            const selected = editorContent.substring(start, end);
            setSelectedText(selected);
            setCursorPosition(start);
        }
    }, [editorContent]);

    // Format text functions
    const insertFormatting = (before: string, after: string = '') => {
        if (editorRef.current) {
            const start = editorRef.current.selectionStart;
            const end = editorRef.current.selectionEnd;
            const selectedText = editorContent.substring(start, end);

            const newText = editorContent.substring(0, start) +
                before + selectedText + after +
                editorContent.substring(end);

            updateContent(newText);

            // Restore cursor position
            setTimeout(() => {
                if (editorRef.current) {
                    const newPos = start + before.length + selectedText.length + after.length;
                    editorRef.current.setSelectionRange(newPos, newPos);
                    editorRef.current.focus();
                }
            }, 0);
        }
    };

    const formatBold = () => insertFormatting('**', '**');
    const formatItalic = () => insertFormatting('*', '*');
    const formatUnderline = () => insertFormatting('<u>', '</u>');
    const formatHeading1 = () => insertFormatting('\n# ', '');
    const formatHeading2 = () => insertFormatting('\n## ', '');
    const formatQuote = () => insertFormatting('\n> ', '');
    const formatCodeBlock = () => insertFormatting('\n```\n', '\n```\n');
    const formatLink = () => {
        const url = prompt('Enter URL:');
        if (url) insertFormatting('[', `](${url})`);
    };

    // Media upload handling
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            for (const file of Array.from(files)) {
                try {
                    const mediaItem = await uploadMedia(file);
                    insertMedia(mediaItem.id, cursorPosition);
                } catch (error) {
                    console.error('Upload failed:', error);
                }
            }
        }
    };

    // Export handling
    const handleExport = async (format: 'html' | 'markdown' | 'pdf') => {
        try {
            await exportDocument(format);
            setShowExportModal(false);
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    // New document handling
    const handleCreateNewDocument = async () => {
        if (!newDocumentTitle.trim()) return;

        await createNewDocument({
            title: newDocumentTitle,
            content: '',
            tags: [],
            seoSettings: {
                title: newDocumentTitle,
                metaDescription: '',
                keywords: [],
                // slug: generateSlug(newDocumentTitle),
            },
        });

        setNewDocumentTitle('');
        setShowNewDocumentModal(false);
    };

    // Version handling
    const handleSaveVersion = async () => {
        if (!versionComment.trim()) return;

        await saveNewVersion(versionComment);
        setVersionComment('');
        setShowSaveVersionModal(false);
    };

    const handleRestoreVersion = async (versionId: string) => {
        if (confirm('Are you sure you want to restore this version? Current changes will be lost.')) {
            await restoreVersion(versionId);
        }
    };

    // Media deletion
    const handleDeleteMedia = (mediaId: string) => {
        if (confirm('Are you sure you want to delete this media file?')) {
            deleteMedia(mediaId);
        }
    };

    // Reset editor
    const handleResetEditor = () => {
        if (confirm('Are you sure you want to reset the editor? All unsaved changes will be lost.')) {
            resetEditor();
        }
    };

    // Simple markdown to HTML converter for preview
    const markdownToHtml = (markdown: string): string => {
        return markdown
            .replace(/^# (.*$)/gim, '<h1>$1</h1>')
            .replace(/^## (.*$)/gim, '<h2>$1</h2>')
            .replace(/^### (.*$)/gim, '<h3>$1</h3>')
            .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
            .replace(/\*(.*)\*/gim, '<em>$1</em>')
            .replace(/<u>(.*)<\/u>/gim, '<u>$1</u>')
            .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
            .replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>')
            .replace(/`([^`]+)`/gim, '<code>$1</code>')
            .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>')
            .replace(/\n/gim, '<br>');
    };

    if (isLoading) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col bg-gray-50 dark:bg-gray-900 ${isFullscreen ? 'fixed top-0 left-0 z-50 w-[133.33vw] h-[133.33vh]' : 'h-[calc(133.33vh-10rem)] rounded-2xl overflow-hidden shadow-sm dark:shadow-none dark:border dark:border-gray-700'}`}>
            {/* Top Navigation */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        {/* Left section */}
                        <div className="flex items-center gap-4">
                            <input
                                type="text"
                                value={currentDocument?.title || ''}
                                onChange={(e) => updateTitle(e.target.value)}
                                className="text-xl font-semibold bg-transparent text-gray-900 dark:text-white border-none outline-none focus:ring-2 focus:ring-primary-500 rounded px-2 py-1"
                                placeholder="Untitled Document"
                            />
                            {hasUnsavedChanges && (
                                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                            )}
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                {currentVersion}
                            </div>
                        </div>

                        {/* Center section - Status & Collaborators */}
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                            {isSaving ? (
                                <div className="flex items-center gap-2">
                                    <Loader className="w-4 h-4 animate-spin" />
                                    <span>Saving...</span>
                                </div>
                            ) : (
                                <span>{formatLastSaved()}</span>
                            )}

                            {/* {currentDocument?.status && (
                                <div className="flex items-center gap-1">
                                    {currentDocument.status === 'published' && <CheckCircle className="w-4 h-4 text-green-500" />}
                                    {currentDocument.status === 'draft' && <Clock className="w-4 h-4 text-gray-400" />}
                                    <span className="capitalize">{currentDocument.status}</span>
                                </div>
                            )}

                            {collaborators.length > 0 && (
                                <div className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    <span>{collaborators.length}</span>
                                </div>
                            )} */}
                        </div>

                        {/* Right section - Actions */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowNewDocumentModal(true)}
                                className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                New
                            </button>

                            <button
                                onClick={saveDocument}
                                disabled={isSaving || !hasUnsavedChanges}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Save className="w-4 h-4" />
                                Save
                            </button>

                            <button
                                onClick={togglePreview}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${showPreview ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                            >
                                <Eye className="w-4 h-4" />
                                Preview
                            </button>

                            <div className="flex items-center">
                                <button
                                    onClick={() => setShowSaveVersionModal(true)}
                                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    title="Save Version"
                                >
                                    <History className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={() => setShowExportModal(true)}
                                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    title="Export"
                                >
                                    <Download className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={handleResetEditor}
                                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    title="Reset Editor"
                                >
                                    <FileX className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toolbar */}
                {showToolbar && (
                    <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center gap-1">
                            <div className="flex items-center gap-1 mr-4">
                                <button onClick={formatBold} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Bold">
                                    <Bold className="w-4 h-4" />
                                </button>
                                <button onClick={formatItalic} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Italic">
                                    <Italic className="w-4 h-4" />
                                </button>
                                <button onClick={formatUnderline} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Underline">
                                    <Underline className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mr-4"></div>

                            <div className="flex items-center gap-1 mr-4">
                                <button onClick={formatHeading1} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Heading 1">
                                    <Heading1 className="w-4 h-4" />
                                </button>
                                <button onClick={formatHeading2} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Heading 2">
                                    <Heading2 className="w-4 h-4" />
                                </button>
                                <button onClick={formatQuote} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Quote">
                                    <Quote className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mr-4"></div>

                            <div className="flex items-center gap-1 mr-4">
                                <button onClick={formatLink} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Link">
                                    <Link className="w-4 h-4" />
                                </button>
                                <button onClick={() => fileInputRef.current?.click()} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Image">
                                    <Image className="w-4 h-4" />
                                </button>
                                <button onClick={toggleMediaLibrary} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Media Library">
                                    <Video className="w-4 h-4" />
                                </button>
                                <button onClick={formatCodeBlock} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Code Block">
                                    <Code className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="w-px h-6 bg-gray-300 mr-4"></div>

                            <div className="flex items-center gap-1">
                                <button onClick={toggleVersionHistory} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="Version History">
                                    <History className="w-4 h-4" />
                                </button>
                                <button onClick={toggleSEOPanel} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors" title="SEO Settings">
                                    <Settings className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Main Editor Layout */}
            <div className="flex-1 flex overflow-hidden">
                {/* Editor Area */}
                <div className="flex-1 flex flex-col">
                    <div className="flex-1 flex">
                        {/* Content Editor */}
                        <div className={`flex-1 ${showPreview ? 'w-1/2' : 'w-full'}`}>
                            <div className="h-full p-6 bg-white dark:bg-gray-900">
                                <textarea
                                    ref={editorRef}
                                    value={editorContent}
                                    onChange={(e) => updateContent(e.target.value)}
                                    onSelect={handleTextSelection}
                                    className="w-full h-full resize-none border-none outline-none text-gray-900 dark:text-gray-100 dark:bg-gray-900 text-lg leading-relaxed font-mono"
                                    placeholder="Start writing your content... Use Markdown for formatting."
                                    spellCheck
                                />
                            </div>
                        </div>

                        {/* Preview Panel */}
                        {showPreview && (
                            <div className="w-1/2 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-y-auto">
                                <div className="p-6">
                                    <div className="prose prose-lg max-w-none">
                                        <div dangerouslySetInnerHTML={{
                                            __html: markdownToHtml(editorContent)
                                        }} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Status Bar */}
                    <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-6">
                            <span>Words: {wordCount.toLocaleString()}</span>
                            <span>Reading time: {readingTime} min</span>
                            <span>Characters: {editorContent.length.toLocaleString()}</span>
                        </div>

                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setShowToolbar(!showToolbar)}
                                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                {showToolbar ? 'Hide Toolbar' : 'Show Toolbar'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                {!sidebarCollapsed && (
                    <div className="w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
                        {/* Document Info */}
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Document Info</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Words:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">{wordCount.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Reading time:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">{readingTime} min</span>
                                </div>
                                {/* <div className="flex justify-between">
                                    <span className="text-gray-600">Status:</span>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${currentDocument?.status === 'published' ? 'bg-green-100 text-green-700' :
                                        'bg-gray-100 text-gray-700'
                                        }`}>
                                        {currentDocument?.status}
                                    </span>
                                </div> */}
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Version:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">{currentVersion}</span>
                                </div>
                            </div>
                        </div>

                        {/* Version History */}
                        {showVersionHistory && (
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Version History</h3>
                                    <button
                                        onClick={() => setShowSaveVersionModal(true)}
                                        className="p-1 text-primary-600 hover:text-primary-700"
                                        title="Save New Version"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    {currentDocument?.versions?.map((version, index) => (
                                        <div key={version._id} className={`flex items-center justify-between p-3 rounded-lg ${index === 0 ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                                            <div className="flex-1">
                                                <div className="font-medium text-sm text-gray-900 dark:text-white">
                                                    {version.version} {index === 0 && '- Current'}
                                                </div>
                                                <div className="text-xs text-gray-600 dark:text-gray-400">{version.comment}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {new Date(version.timestamp).toLocaleDateString()}
                                                </span>
                                                {index > 0 && (
                                                    <button
                                                        onClick={() => handleRestoreVersion(version._id)}
                                                        className="p-1 text-gray-500 hover:text-primary-600"
                                                        title="Restore Version"
                                                    >
                                                        <RotateCcw className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {(!currentDocument?.versions || currentDocument.versions.length === 0) && (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        No versions yet. Save a version to track changes.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* SEO Settings */}
                        {showSEOPanel && currentDocument && (
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">SEO Settings</h3>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            SEO Title
                                        </label>
                                        <input
                                            type="text"
                                            value={currentDocument.seoSettings.title || ''}
                                            onChange={(e) => updateSEOSettings({ title: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            placeholder="Enter SEO title..."
                                        />
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {(currentDocument.seoSettings.title || '').length}/60 characters
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Meta Description
                                        </label>
                                        <textarea
                                            value={currentDocument.seoSettings.metaDescription || ''}
                                            onChange={(e) => updateSEOSettings({ metaDescription: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                                            rows={3}
                                            placeholder="Enter meta description..."
                                        />
                                        <div className="text-xs text-gray-500 mt-1">
                                            {(currentDocument.seoSettings.metaDescription || '').length}/160 characters
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Focus Keyword
                                        </label>
                                        <input
                                            type="text"
                                            value={currentDocument.seoSettings.keywords?.[0] || ''}
                                            onChange={(e) => updateSEOSettings({ keywords: [e.target.value, ...(currentDocument.seoSettings.keywords?.slice(1) || [])] })}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            placeholder="Enter focus keyword..."
                                        />
                                    </div>
                                    {/* 
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            URL Slug
                                        </label>
                                        <input
                                            type="text"
                                            value={currentDocument.seoSettings.slug || ''}
                                            onChange={(e) => updateSEOSettings({ slug: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            placeholder="auto-generated-slug"
                                        />
                                        <button
                                            onClick={() => updateSEOSettings({ slug: generateSlug(currentDocument.title) })}
                                            className="text-xs text-primary-600 hover:text-primary-700 mt-1"
                                        >
                                            Generate from title
                                        </button>
                                    </div> */}
                                </div>
                            </div>
                        )}

                        {/* Media Library */}
                        {showMediaLibrary && (
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Media Library</h3>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    {mediaLibrary.map(media => (
                                        <div
                                            key={media.id}
                                            className="group relative bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden"
                                        >
                                            {media.type === 'image' ? (
                                                <img
                                                    src={media.thumbnail || media.url}
                                                    alt={media.name}
                                                    className="w-full h-20 object-cover cursor-pointer"
                                                    onClick={() => insertMedia(media.id, cursorPosition)}
                                                />
                                            ) : (
                                                <div
                                                    className="w-full h-20 flex items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                                    onClick={() => insertMedia(media.id, cursorPosition)}
                                                >
                                                    {media.type === 'video' ? (
                                                        <Video className="w-8 h-8 text-gray-500" />
                                                    ) : (
                                                        <FileText className="w-8 h-8 text-gray-500" />
                                                    )}
                                                </div>
                                            )}

                                            {/* Media actions overlay */}
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => insertMedia(media.id, cursorPosition)}
                                                        className="p-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors"
                                                        title="Insert Media"
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteMedia(media.id)}
                                                        className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                                                        title="Delete Media"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Media info */}
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                                                <div className="text-white text-xs truncate">
                                                    {media.name}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {mediaLibrary.length === 0 && (
                                    <div className="text-center py-8">
                                        <Image className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                        <p className="text-sm text-gray-600 dark:text-gray-400">No media files yet</p>
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="text-sm text-primary-600 hover:text-primary-700 font-medium mt-2"
                                        >
                                            Upload your first file
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Recent Documents */}
                        {recentDocuments.length > 0 && (
                            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Recent Documents</h3>
                                <div className="space-y-2">
                                    {recentDocuments.slice(0, 5).map((doc) => (
                                        <div
                                            key={doc._id}
                                            className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition-colors"
                                            onClick={() => loadDocument && loadDocument(doc._id)}
                                        >
                                            <FileText className="w-4 h-4 text-gray-400" />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm truncate text-gray-900 dark:text-white">{doc.title}</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {new Date(doc.updatedAt).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Sidebar Toggle */}
                <button
                    onClick={toggleSidebar}
                    className="absolute top-1/2 right-0 transform -translate-y-1/2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-l-lg p-2 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors z-10"
                    style={{ right: sidebarCollapsed ? '0' : '320px' }}
                >
                    {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronRight className="w-4 h-4 rotate-180" />}
                </button>
            </div>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
            />

            {/* New Document Modal */}
            {showNewDocumentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create New Document</h3>
                            <button
                                onClick={() => setShowNewDocumentModal(false)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Document Title
                                </label>
                                <input
                                    type="text"
                                    value={newDocumentTitle}
                                    onChange={(e) => setNewDocumentTitle(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    placeholder="Enter document title..."
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setShowNewDocumentModal(false)}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreateNewDocument}
                                    disabled={!newDocumentTitle.trim()}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Create Document
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Save Version Modal */}
            {showSaveVersionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Save New Version</h3>
                            <button
                                onClick={() => setShowSaveVersionModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Version Notes
                                </label>
                                <textarea
                                    value={versionComment}
                                    onChange={(e) => setVersionComment(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                                    rows={3}
                                    placeholder="Describe what changed in this version..."
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setShowSaveVersionModal(false)}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveVersion}
                                    disabled={!versionComment.trim()}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Save Version
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Export Modal */}
            {showExportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Export Document</h3>
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <button
                                onClick={() => handleExport('markdown')}
                                className="w-full flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <FileText className="w-5 h-5 text-gray-500" />
                                <div className="text-left">
                                    <div className="font-medium text-gray-900 dark:text-white">Markdown</div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">Export as .md file</div>
                                </div>
                            </button>

                            <button
                                onClick={() => handleExport('html')}
                                className="w-full flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <Code className="w-5 h-5 text-gray-500" />
                                <div className="text-left">
                                    <div className="font-medium text-gray-900 dark:text-white">HTML</div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">Export as .html file</div>
                                </div>
                            </button>

                            <button
                                onClick={() => handleExport('pdf')}
                                className="w-full flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <FileText className="w-5 h-5 text-gray-500" />
                                <div className="text-left">
                                    <div className="font-medium text-gray-900 dark:text-white">PDF</div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">Export as .pdf file</div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContentEditorPage;